import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Trash2, Plus, Camera, ImageIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_app/_admin/admin/settings/ads")({
  component: AdsSettingsPage,
});
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

const ROLE_OPTIONS: { key: string; label: string; locked?: boolean }[] = [
  { key: "admin", label: "Admin", locked: true },
  { key: "moderator", label: "Moderator" },
  { key: "marketer", label: "Marketer" },
  { key: "seo", label: "SEO" },
  { key: "editor", label: "Editor" },
  { key: "designer", label: "Graphic Designer" },
  { key: "scriptwriter", label: "Scriptwriter" },
  { key: "smm", label: "SMM" },
  { key: "calling", label: "Calling" },
  { key: "hr", label: "HR" },
];

interface SheetConfig {
  id: string;
  config_key: string;
  label: string | null;
  refresh_note: string | null;
  sheet_url: string | null;
  sheet_tab: string | null;
}

export function AdsSettingsPage() {
  const { role } = useAuth();
  const isAdmin = role === "admin";

  const sheetsQ = useQuery({
    queryKey: ["ads_sheet_config_all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ads_sheet_config")
        .select("*")
        .order("config_key");
      if (error) throw error;
      return data as SheetConfig[];
    },
  });

  const permsQ = useQuery({
    queryKey: ["ads_section_permissions_all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ads_section_permissions")
        .select("*");
      if (error) throw error;
      return data as { role: string; can_access: boolean }[];
    },
  });

  return (
    <div className="space-y-6 px-[22px] py-4">
      <section>
        <h2 className="text-[16px] font-bold">Ads Performance — Data Sources</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Paste the Google Sheet URL and the exact tab name for each data source.
        </p>
        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {sheetsQ.data?.map((c) => (
            <SheetConfigCard key={c.id} cfg={c} disabled={!isAdmin} onSaved={() => sheetsQ.refetch()} />
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-[16px] font-bold">Ads Performance — Access Permissions</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Choose which roles can see the Ads Performance section in the sidebar.
        </p>
        <div className="mt-4 grid gap-2 rounded-lg border border-border bg-card p-4 shadow-sm sm:grid-cols-2">
          {ROLE_OPTIONS.map((r) => {
            const cur = permsQ.data?.find((p) => p.role === r.key);
            return (
              <PermRow
                key={r.key}
                roleKey={r.key}
                label={r.label}
                checked={!!cur?.can_access}
                locked={r.locked || !isAdmin}
                onToggled={() => permsQ.refetch()}
              />
            );
          })}
        </div>
      </section>

      <ProductCampaignMappingSection isAdmin={isAdmin} />
    </div>
  );
}

function SheetConfigCard({
  cfg,
  disabled,
  onSaved,
}: {
  cfg: SheetConfig;
  disabled: boolean;
  onSaved: () => void;
}) {
  const [url, setUrl] = useState(cfg.sheet_url ?? "");
  const [tab, setTab] = useState(cfg.sheet_tab ?? "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setUrl(cfg.sheet_url ?? "");
    setTab(cfg.sheet_tab ?? "");
  }, [cfg.sheet_url, cfg.sheet_tab]);

  const save = async () => {
    setBusy(true);
    const { error } = await supabase
      .from("ads_sheet_config")
      .update({ sheet_url: url || null, sheet_tab: tab || null })
      .eq("id", cfg.id);
    setBusy(false);
    if (error) toast.error(error.message);
    else {
      toast.success(`${cfg.label} saved`);
      onSaved();
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="text-[13px] font-bold text-foreground">{cfg.label}</div>
        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
          {cfg.config_key}
        </span>
      </div>
      {cfg.refresh_note && (
        <div className="mt-1 text-[11px] text-muted-foreground">{cfg.refresh_note}</div>
      )}
      <div className="mt-3 space-y-2">
        <div className="space-y-1">
          <Label className="text-[10px] uppercase text-muted-foreground">Sheet URL</Label>
          <Input
            value={url}
            disabled={disabled}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://docs.google.com/spreadsheets/d/..."
            className="h-8 text-[12px]"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] uppercase text-muted-foreground">Sheet Tab Name</Label>
          <Input
            value={tab}
            disabled={disabled}
            onChange={(e) => setTab(e.target.value)}
            placeholder="e.g. Sheet1"
            className="h-8 text-[12px]"
          />
        </div>
        <div className="pt-1">
          <Button
            size="sm"
            onClick={save}
            disabled={busy || disabled}
            style={{ background: "#18181b" }}
            className="h-8 text-[12px] text-white hover:opacity-90"
          >
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PermRow({
  roleKey,
  label,
  checked,
  locked,
  onToggled,
}: {
  roleKey: string;
  label: string;
  checked: boolean;
  locked: boolean;
  onToggled: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const toggle = async (next: boolean) => {
    setBusy(true);
    const { error } = await supabase
      .from("ads_section_permissions")
      .update({ can_access: next })
      .eq("role", roleKey);
    setBusy(false);
    if (error) toast.error(error.message);
    else {
      toast.success(`${label}: ${next ? "enabled" : "disabled"}`);
      onToggled();
    }
  };
  return (
    <div className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-2">
      <span className="text-[12px] font-medium">{label}</span>
      <Switch checked={checked} disabled={locked || busy} onCheckedChange={toggle} />
    </div>
  );
}

// ----------------- Product ↔ Campaign Mapping -----------------
interface MappingRow {
  id: string;
  product_keyword: string;
  campaign_code: string;
  display_name: string | null;
  image_url: string | null;
  _isNew?: boolean;
  _dirty?: boolean;
}

function ProductCampaignMappingSection({ isAdmin }: { isAdmin: boolean }) {
  const q = useQuery({
    queryKey: ["product_campaign_mappings"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("product_campaign_mappings")
        .select("*")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as MappingRow[];
    },
  });

  const [rows, setRows] = useState<MappingRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  useEffect(() => {
    if (q.data) setRows(q.data.map((r) => ({ ...r })));
  }, [q.data]);

  const update = (idx: number, patch: Partial<MappingRow>) =>
    setRows((rs) =>
      rs.map((r, i) => (i === idx ? { ...r, ...patch, _dirty: true } : r)),
    );

  const addRow = () => {
    setRows((rs) => [
      ...rs,
      {
        id: `new-${Math.random().toString(36).slice(2, 9)}`,
        product_keyword: "",
        campaign_code: "",
        display_name: "",
        image_url: null,
        _isNew: true,
        _dirty: true,
      },
    ]);
  };

  const deleteRow = async (row: MappingRow) => {
    if (row._isNew) {
      setRows((rs) => rs.filter((r) => r.id !== row.id));
      setPendingDelete(null);
      return;
    }
    setBusy(true);
    const { error } = await (supabase as any)
      .from("product_campaign_mappings")
      .delete()
      .eq("id", row.id);
    setBusy(false);
    setPendingDelete(null);
    if (error) toast.error(error.message);
    else {
      toast.success("Mapping deleted");
      q.refetch();
    }
  };

  const saveAll = async () => {
    const toInsert = rows
      .filter((r) => r._isNew && r.product_keyword.trim() && r.campaign_code.trim())
      .map((r) => ({
        product_keyword: r.product_keyword.trim(),
        campaign_code: r.campaign_code.trim(),
        display_name: r.display_name?.trim() || null,
        image_url: r.image_url?.trim() || null,
      }));
    const toUpdate = rows
      .filter(
        (r) =>
          !r._isNew &&
          r._dirty &&
          r.product_keyword.trim() &&
          r.campaign_code.trim(),
      )
      .map((r) => ({
        id: r.id,
        product_keyword: r.product_keyword.trim(),
        campaign_code: r.campaign_code.trim(),
        display_name: r.display_name?.trim() || null,
        image_url: r.image_url?.trim() || null,
      }));

    setBusy(true);
    try {
      if (toInsert.length) {
        const { error } = await (supabase as any)
          .from("product_campaign_mappings")
          .insert(toInsert);
        if (error) throw error;
      }
      for (const u of toUpdate) {
        const { id, ...patch } = u;
        const { error } = await (supabase as any)
          .from("product_campaign_mappings")
          .update(patch)
          .eq("id", id);
        if (error) throw error;
      }
      toast.success(
        `Saved (${toInsert.length} added, ${toUpdate.length} updated)`,
      );
      q.refetch();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const dirtyCount = useMemo(
    () => rows.filter((r) => r._dirty || r._isNew).length,
    [rows],
  );

  return (
    <section>
      <h2 className="text-[16px] font-bold">Product ↔ Campaign Mapping</h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Map Shopify product names to Meta ad campaign codes. Used to calculate
        Meta orders per product on the Orders tab.
      </p>
      <div className="mt-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-[11px] text-blue-800">
        <strong>How it works:</strong> If a Shopify order contains{" "}
        <code className="font-mono">emblazer</code> in the product title AND a Meta ad contains{" "}
        <code className="font-mono">EMZZ</code> in the ad name, their data is merged
        onto the same product card.
      </div>

      <div className="mt-4 overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        <div className="grid grid-cols-[32px_140px_1fr_1fr_1fr_44px] gap-2 border-b border-border bg-muted/40 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          <span>#</span>
          <span>Product Image</span>
          <span>Product Keyword (contains)</span>
          <span>Campaign Code (contains)</span>
          <span>Display Name</span>
          <span></span>
        </div>
        {q.isLoading ? (
          <div className="p-6 text-center text-[12px] text-muted-foreground">
            Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-[12px] text-muted-foreground">
            No mappings yet. Click <strong>+ Add Mapping</strong> below.
          </div>
        ) : (
          rows.map((r, idx) => (
            <div
              key={r.id}
              className="grid grid-cols-[32px_140px_1fr_1fr_1fr_44px] gap-2 border-b border-border px-3 py-2 last:border-b-0"
            >
              <span className="self-center text-[11px] text-muted-foreground">
                {idx + 1}
              </span>
              <ImageCell
                value={r.image_url}
                disabled={!isAdmin}
                onChange={(v) => update(idx, { image_url: v })}
              />
              <div>
                <Input
                  value={r.product_keyword}
                  disabled={!isAdmin}
                  onChange={(e) =>
                    update(idx, { product_keyword: e.target.value })
                  }
                  placeholder="e.g. emblazer"
                  className="h-8 text-[12px]"
                />
                <div className="mt-0.5 text-[10px] text-muted-foreground">
                  Must appear in the Shopify product title
                </div>
              </div>
              <div>
                <Input
                  value={r.campaign_code}
                  disabled={!isAdmin}
                  onChange={(e) =>
                    update(idx, { campaign_code: e.target.value })
                  }
                  placeholder="e.g. EMZZ"
                  className="h-8 text-[12px]"
                />
                <div className="mt-0.5 text-[10px] text-muted-foreground">
                  Must appear in Meta Ad Name or Campaign Name
                </div>
              </div>
              <div>
                <Input
                  value={r.display_name ?? ""}
                  disabled={!isAdmin}
                  onChange={(e) =>
                    update(idx, { display_name: e.target.value })
                  }
                  placeholder="e.g. Car Door Sill Protector"
                  className="h-8 text-[12px]"
                />
                <div className="mt-0.5 text-[10px] text-muted-foreground">
                  Card title on Orders tab
                </div>
              </div>
              <div className="self-start pt-1">
                {pendingDelete === r.id ? (
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => deleteRow(r)}
                      disabled={busy}
                      className="rounded bg-red-600 px-1.5 py-0.5 text-[9px] font-bold text-white hover:bg-red-700"
                    >
                      Confirm
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingDelete(null)}
                      className="rounded border border-border px-1.5 py-0.5 text-[9px]"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={!isAdmin}
                    onClick={() => setPendingDelete(r.id)}
                    className="rounded border border-border p-1.5 text-muted-foreground hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                    aria-label="Delete row"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={addRow}
          disabled={!isAdmin}
          className="h-8 gap-1 text-[12px]"
        >
          <Plus className="h-3.5 w-3.5" /> Add Mapping
        </Button>
        <Button
          size="sm"
          onClick={saveAll}
          disabled={busy || !isAdmin || dirtyCount === 0}
          style={{ background: "#18181b" }}
          className="h-8 text-[12px] text-white hover:opacity-90"
        >
          {busy ? "Saving…" : `Save All${dirtyCount ? ` (${dirtyCount})` : ""}`}
        </Button>
      </div>
    </section>
  );
}

function ImageCell({
  value,
  disabled,
  onChange,
}: {
  value: string | null;
  disabled: boolean;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value ?? "");

  useEffect(() => {
    setDraft(value ?? "");
  }, [value]);

  const save = () => {
    onChange(draft.trim());
    setOpen(false);
  };

  return (
    <div className="self-start">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className="group relative block h-[67px] w-[120px] overflow-hidden rounded-md border border-border bg-muted text-muted-foreground hover:bg-muted/70 disabled:opacity-60"
            style={{ aspectRatio: "16 / 9" }}
            aria-label={value ? "Change image" : "Add image"}
          >
            {value ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={value}
                alt=""
                className="h-full w-full object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            ) : (
              <div className="flex h-full w-full flex-col items-center justify-center gap-0.5">
                <Camera className="h-4 w-4" />
                <span className="text-[9px] font-semibold">Add image</span>
              </div>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-3">
          <div className="space-y-2">
            <Label className="text-[10px] font-semibold uppercase text-muted-foreground">
              Image URL
            </Label>
            <Input
              type="url"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="https://..."
              className="h-8 text-[12px]"
              autoFocus
            />
            <p className="text-[10px] text-muted-foreground">
              Use a direct image link (JPG, PNG, WebP)
            </p>
            {draft && (
              <div className="overflow-hidden rounded-md border border-border bg-muted">
                <img
                  src={draft}
                  alt=""
                  className="h-24 w-full object-cover"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = "none";
                  }}
                />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setOpen(false)}
                className="h-7 text-[11px]"
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={save}
                style={{ background: "#18181b" }}
                className="h-7 text-[11px] text-white hover:opacity-90"
              >
                <ImageIcon className="mr-1 h-3 w-3" /> Save
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
